import CryptoJS from 'crypto-js';
import axios from 'axios';
import {
  redirectToLoginAfterAuthFailure,
  rejectIfAuthSessionExpired,
  resetAuthSessionExpired,
} from './authSessionGuard';

// Encryption key - in production, this should be stored securely
const ENCRYPTION_KEY = 'gvhbfijsadfkefjnujrbghj';

// Interface for user data
interface UserData {
  Type: string[];
  exists: boolean;
  access: string[];
  Name: string;
  Id: string;
  access_token: string;
}

// Encrypt data before storing
export const encryptData = (data: any): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

// Decrypt data from storage
export const decryptData = (encryptedData: string): any => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

// Store encrypted user data
export const storeUserData = (userData: UserData): void => {
  // New login — allow API traffic again after a prior 401 storm stop.
  resetAuthSessionExpired();
  const encryptedData = encryptData(userData);
  localStorage.setItem('userData', encryptedData);
  sessionStorage.setItem('access_token', userData.access_token);
  sessionStorage.setItem('Name', userData.Name);
  sessionStorage.setItem('Id', userData.Id);
};

// Get decrypted user data
export const getUserData = (): UserData | null => {
  const encryptedData = localStorage.getItem('userData');
  if (!encryptedData) return null;
  
  return decryptData(encryptedData);
};

// Get access token
export const getAccessToken = (): string | null => {
  return sessionStorage.getItem('access_token');
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  const userData = getUserData();
  return !!(token && userData && userData.exists);
};

// Clear all stored data
export const clearUserData = (): void => {
  localStorage.removeItem('userData');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('Name');
  sessionStorage.removeItem('Id');
  sessionStorage.removeItem('Admin');
  sessionStorage.removeItem('Trainer');
  sessionStorage.removeItem('Coordinator');
};

// Create axios instance with authentication interceptor
export const createAuthenticatedAxios = () => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL
  });

  // Request interceptor to add bearer token
  instance.interceptors.request.use(
    (config) => {
      const blocked = rejectIfAuthSessionExpired();
      if (blocked) {
        return Promise.reject(blocked);
      }
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle authentication errors
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // Session expiry uses 401. Do not treat business 403 as logout.
      if (error.response?.status === 401) {
        redirectToLoginAfterAuthFailure(clearUserData);
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Check authentication before making API calls
export const checkAuthAndRedirect = (): boolean => {
  if (!isAuthenticated()) {
    clearUserData();
    window.location.href = '/';
    return false;
  }
  return true;
}; 